import React, { useState } from "react";
import { getCurrentSearchNested } from "../api/internal/usersInternalApi.js"
import { mapFieldToModel, relationMapping } from "./databaseParser.js";

function sortObject (data, primarySortKey = "displayName", secondarySortKey = "resourceDisplayName") {
    if (Array.isArray(data)) {
        return data.slice().sort((a, b) => {
            if (typeof a === "object" && typeof b === "object") {
                const aValue = a[primarySortKey] || a[secondarySortKey] || "";
                const bValue = b[primarySortKey] || b[secondarySortKey] || "";
                return aValue.localeCompare(bValue);
            }
            return a > b ? 1 : -1;
        });
    } else if (typeof data === "object" && data !== null) {
        return Object.fromEntries(
            Object.entries(data).sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        );
    }
    return data;
}

function areObjectsEqual(obj1, obj2) {
    return Object.keys(obj1).length === Object.keys(obj2).length && Object.keys(obj1).every(key => obj1[key] === obj2[key]);
}

function checkDifference(differences, key, data) {
    if (!differences || !key || data == undefined) return false;
    
    let differenceEntry = null;
    let firstKey = key.split('.')[0];

    if (typeof differences[firstKey] === "object") {
        differenceEntry = differences[firstKey];

        if (differenceEntry?.value1 || differenceEntry?.value2) {
            if (Array.isArray(key.split('.').slice(1).reduce((acc, part) => acc?.[part], differenceEntry.value1)) || Array.isArray(key.split('.').slice(1).reduce((acc, part) => acc?.[part], differenceEntry.value2)) && !Array.isArray(data) && typeof data !== "object") {
                console.log(data);
                return key.split('.').slice(1).reduce((acc, part) => acc?.[part], differenceEntry.value1)?.includes(data) !== key.split('.').slice(1).reduce((acc, part) => acc?.[part], differenceEntry.value2)?.includes(data);                
            } else {
                const value1 = key.split('.').slice(1).reduce((acc, part) => acc?.[part], differenceEntry.value1);
                const value2 = key.split('.').slice(1).reduce((acc, part) => acc?.[part], differenceEntry.value2);
                return value1 !== value2;
            }
        }
    } else {
        // Accéder dynamiquement aux différences via la clé
        differenceEntry = key.split('.').reduce((acc, part) => acc?.[part], differences);
    }

    if (!differenceEntry) return false;

    // Cas où la valeur est un tableau
    if (Array.isArray(data)) {
        return Array.isArray(differenceEntry)
            ? data.some(item => differenceEntry.some(diffItem => areObjectsEqual(item, diffItem)))
            : differenceEntry.includes(data);
    }

    // Cas où la valeur est un objet
    if (typeof data === "object" && data !== null) {
        return Array.isArray(differenceEntry)
            ? differenceEntry.some(diffItem => areObjectsEqual(data, diffItem))
            : Object.keys(differenceEntry).some(subKey => checkDifference(differenceEntry, subKey, data[subKey]));
    }

    // Comparaison de valeurs simples
    return differenceEntry.includes(data);
}

async function handleClick(filterRow, signal) {
    try {
        const detail = await getCurrentSearchNested(filterRow, signal);
        return detail;
    } catch (error) {
        console.error("Erreur lors de la récupération des détails :", error);
    }
}

export function RenderNestedObject({ data, exclude, models, onClickDetail, primaryKey, diffPageRender, differences }) { // data: object, exclude: string[], models (informations, groups, etc.): string, onClickDetail (nestedDetail): function, primaryKey (json key): string, diffPageRender: bool, differences: object
    data = sortObject(data);
    const [visibleItems, setVisibleItems] = useState({});

    const toggleVisibility = (key) => {
        setVisibleItems((prevState) => ({
            ...prevState,
            [key]: !prevState[key], // Change l'état uniquement pour cet élément
        }));
    };

    if (Array.isArray(data) && data.length !== 0) {
        const relationConfig = relationMapping[primaryKey];
        // Si c'est un tableau
        return (
            <div className="detail-div-array">
                {data.map((item, index) => {
                    const isDifferent = checkDifference(differences, primaryKey, item);
                    const formattedValue = relationConfig
                        ? relationConfig.fields
                            .map(field => field.split('.').reduce((acc, primaryKey) => acc?.[primaryKey], item))
                            .filter(Boolean)
                            .join(relationConfig.separator || " / ")
                        : item;
                    
                    if (typeof item === 'object' && !Array.isArray(item)) {
                        // eslint-disable-next-line react/jsx-key
                        return <RenderNestedObject data={item} exclude={exclude} models={models} onClickDetail={onClickDetail} primaryKey={primaryKey} diffPageRender={false} differences={differences} />
                    } else if (models != null) {
                        return (
                            <button 
                                className="detail-link"
                                onClick={async () => {
                                    const filterRow = [{
                                        id: 1,
                                        operator: 'AND',
                                        comparator: 'equals',
                                        information: models,
                                        propertie: 'objectId',
                                        input: item.objectId
                                    }];

                                    const detail = await handleClick(filterRow);
                                    if (detail) {
                                        onClickDetail(detail[0]);
                                    }
                                }} 
                                key={`detail-link-${index}`}
                            >
                                {formattedValue}
                            </button>
                        );
                    } else {
                        return (
                            <span key={index} className={"detail-value" + (isDifferent ? ' highlight' : '')}>
                                {formattedValue}
                            </span>
                        );
                    }
                })}
            </div>
        );
    } else if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
        const isDifferent = checkDifference(differences, primaryKey, data);
        const relationConfig = relationMapping[primaryKey];
        const formattedValue = relationConfig
            ? relationConfig.fields
                .map(field => field.split('.').reduce((acc, key) => acc?.[key], data))
                .filter(Boolean)
                .join(relationConfig.separator || " / ")
            : data;

        if (primaryKey !== undefined && relationMapping[primaryKey]) {
            return models != null ? (
                <button
                    className="detail-link"
                    onClick={async () => {
                        const filterRow = [{
                            id: 1,
                            operator: 'AND',
                            comparator: 'equals',
                            information: models,
                            propertie: 'objectId',
                            input: data.objectId
                        }];

                        const detail = await handleClick(filterRow);
                        if (detail) onClickDetail(detail[0]);
                    }}
                    key={`detail-${primaryKey}-${data.objectId}`}
                >
                    {formattedValue}
                </button>
            ) : (
                <span key={`detail-${primaryKey}-${data.objectId}`} className={"detail-value" + (isDifferent ? ' highlight' : '')}>
                    {formattedValue}
                </span>
            );
        }
        
        return (
            <div className={"detail-div-master" + (isDifferent ? ' highlight' : '')}>
                {Object.entries(data).map(([key, value]) => {
                    if (exclude.includes(key)) return null;
                    const isDifferent = checkDifference(differences, primaryKey ? `${primaryKey}.${key}` : key, value);
                    const isVisible = visibleItems[primaryKey ? `${primaryKey}.${key}` : key] || false;
                    const uniqueKey = primaryKey ? `${primaryKey}.${key}` : key;

                    if (Array.isArray(value)) {
                        // Récursivement traiter un tableau
                        return (
                            <div className="detail-div-bis" key={key}>
                                <div style={{display: "flex", alignItems: "center"}}>
                                    <p className="detail-key">{key} :</p>
                                    <i className={(isVisible ? "bx bx-chevrons-down" : "bx bx-chevrons-up")} style={{cursor: "pointer"}} onClick={() => toggleVisibility(uniqueKey)}></i>
                                </div>
                                <div className={(isVisible ? 'active' : 'inactive')}>
                                    <RenderNestedObject data={value} exclude={exclude} models={diffPageRender && mapFieldToModel(key) || undefined} onClickDetail={onClickDetail} primaryKey={uniqueKey} diffPageRender={false} differences={differences} />
                                </div>
                            </div>
                        );
                    } else if (typeof value === "object" && value !== null) {
                        // Récursivement traiter un objet imbriqué
                        return (
                            <div key={key} className="detail-div-nested">
                                <p className="detail-key">{key} :</p>
                                <div className="nested-object">
                                    <RenderNestedObject data={value} exclude={exclude} models={undefined} onClickDetail={onClickDetail} primaryKey={uniqueKey} diffPageRender={false} differences={differences} />
                                </div>
                            </div>
                        );
                    } else if (value !== null) {
                        // Rendre une clé-valeur simple
                        return (
                            <div key={key} className={"detail-div" + (isDifferent ? ' highlight' : '')}>
                                <p className="detail-key">{key} :</p>
                                <p className="detail-value">{value.toString()}</p>
                            </div>
                        );
                    }
                })}
            </div>
        );
    }
}